// Thin, safe wrapper around GA4's gtag() so the app can fire e-commerce events
// without every call site having to null-check window.gtag or bikeshed on
// payload shape. Payloads follow GA4's recommended e-commerce spec:
// https://developers.google.com/analytics/devguides/collection/ga4/ecommerce
//
// Fire-and-forget: if GA4 is disabled or the library hasn't loaded yet, calls
// are silently dropped. Never blocks the UI. Never throws.

const GA_CURRENCY = "USD";

const sanitizePrice = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.max(0, Math.round(n * 100) / 100) : 0;
};

const buildItem = (p, qty = 1) => ({
  item_id: p.sku || p.slug || p.id || "unknown",
  item_name: p.name || p.slug || "Unknown",
  item_category: p.category_slug || p.category || undefined,
  item_variant: p.variant || undefined,
  price: sanitizePrice(p.price),
  quantity: Math.max(1, parseInt(qty, 10) || 1),
});

const send = (eventName, params) => {
  if (typeof window === "undefined") return;
  if (typeof window.gtag !== "function") return;
  try {
    window.gtag("event", eventName, params);
  } catch {
    // never let analytics break the app
  }
};

export const gaViewItem = (product) => {
  if (!product) return;
  const item = buildItem(product);
  send("view_item", { currency: GA_CURRENCY, value: item.price, items: [item] });
};

export const gaAddToCart = (product, qty = 1) => {
  if (!product) return;
  const item = buildItem(product, qty);
  send("add_to_cart", { currency: GA_CURRENCY, value: item.price * item.quantity, items: [item] });
};

export const gaRemoveFromCart = (product, qty = 1) => {
  if (!product) return;
  const item = buildItem(product, qty);
  send("remove_from_cart", { currency: GA_CURRENCY, value: item.price * item.quantity, items: [item] });
};

export const gaBeginCheckout = (cartItems, total) => {
  if (!Array.isArray(cartItems) || cartItems.length === 0) return;
  send("begin_checkout", {
    currency: GA_CURRENCY,
    value: sanitizePrice(total),
    items: cartItems.map((i) => buildItem(i, i.quantity)),
  });
};

// GA4 dedupes purchases by transaction_id, so passing the Stripe session id
// is enough — refresh spam on the success page won't inflate revenue.
export const gaPurchase = ({ transactionId, value, items }) => {
  if (!transactionId || !Array.isArray(items) || items.length === 0) return;
  send("purchase", {
    transaction_id: transactionId,
    currency: GA_CURRENCY,
    value: sanitizePrice(value),
    items: items.map((i) => buildItem(i, i.quantity)),
  });
};
